import React from "react";

import { FormProvider, UseFormReturn } from "react-hook-form";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

import { AutocompleteCloseReason } from "@mui/base";

import {
  AddressAutocompletePrecisely,
  PreciselyAddressWithUnit,
  ViewEnteredAddressOnMap,
} from "@covertree/features";
import { GeoCodeInput } from "@covertree/new-business-graphql";
import { Form, FormTextField, SubmitButton } from "@covertree/ui";

import { HomeAddressForm } from "./form";

interface HomeAddressViewProps {
  handleMapBoxOpen: (geoCodeInput: GeoCodeInput) => void;
  homeAddressForm: UseFormReturn<HomeAddressForm>;
  isNextButtonVisible: boolean;
  loading: boolean;
  onSubmit: (formValues: HomeAddressForm) => Promise<void>;
  onClose: (
    event: React.SyntheticEvent,
    reason: AutocompleteCloseReason
  ) => void;
  onBlur: () => void;
}

export function HomeAddressView({
  handleMapBoxOpen,
  homeAddressForm,
  isNextButtonVisible,
  loading,
  onSubmit,
  onClose,
  onBlur,
}: HomeAddressViewProps) {
  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid },
  } = homeAddressForm;

  const address = watch("address");
  const unit = watch("unit");

  return (
    <FormProvider {...homeAddressForm}>
      <Form onSubmit={handleSubmit(onSubmit)} onBlur={onBlur}>
        <Grid container spacing={2}>
          <Grid item md={8} xs={12}>
            <AddressAutocompletePrecisely
              id="potential-policy-address"
              name="address"
              label="Property Address"
              control={control}
              onClose={onClose}
            />
          </Grid>

          <Grid item md={4} xs={12}>
            <FormTextField
              id="potential-unit"
              name="unit"
              label="Unit / Lot#"
              control={control}
              tooltip="Provided value will override selected Unit/Lot#"
              optional
            />
          </Grid>
        </Grid>

        <Box my={1}>
          <ViewEnteredAddressOnMap
            isValid={isValid}
            address={{
              ...(address?.value as PreciselyAddressWithUnit),
              ...(unit ? { lotUnit: unit } : {}),
            }}
            onOpen={handleMapBoxOpen}
          />
        </Box>

        {isNextButtonVisible ? (
          <SubmitButton
            loading={loading}
            disabled={!isValid || loading}
            width={100}>
            Next
          </SubmitButton>
        ) : null}
      </Form>
    </FormProvider>
  );
}
